import { useCallback, useEffect, useMemo, useState } from 'react';
import { groupBy } from 'lodash';
import { Client, useClient } from 'urql';
import {
  OperationResult,
  ScaledGeometricResult,
  ResultType,
} from '../../../domain/geometry/geometric-types';
import { GetAlgorithmGeometryDocument } from '../../../gql/graphql';
import { useMeshAlgorithmResults } from '../../../hooks/mesh-algorithm-results';
import { useViewer } from '../../../components/common/ForgeViewer';
import { GeometricResultVisualization } from './GeometricResultVisualization';

/**
 * Check if an operation result already has geometry data
 */
function hasGeometry(result: OperationResult): boolean {
  const [scaledResult] = result;
  if (!scaledResult) return false;

  // Use type assertion to check for geometry properties
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const anyResult = scaledResult as any;
  return Boolean(
    anyResult.triangles ||
      anyResult.multiPolygon ||
      anyResult.multiPolyline ||
      anyResult.point
  );
}

interface FetchedGeometry {
  type: number;
  triangles?: unknown;
  multiPolygon?: unknown;
  multiPolyline?: unknown;
  point?: unknown;
  plane?: { a: number; b: number; c: number; d: number } | null;
}

/**
 * Merge fetched geometry into a ScaledGeometricResult
 */
function mergeGeometry(
  result: NonNullable<ScaledGeometricResult>,
  geometry: FetchedGeometry
): ScaledGeometricResult {
  // Build the merged result with geometry
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const merged: any = {
    value: result.value,
    units: result.units,
    type: geometry.type as ResultType,
  };

  if (geometry.triangles) {
    merged.triangles = geometry.triangles;
  }
  if (geometry.multiPolygon) {
    merged.multiPolygon = geometry.multiPolygon;
  }
  if (geometry.multiPolyline) {
    merged.multiPolyline = geometry.multiPolyline;
  }
  if (geometry.point) {
    merged.point = geometry.point;
  }
  if (geometry.plane) {
    merged.plane = geometry.plane;
  }

  return merged as ScaledGeometricResult;
}

/**
 * Build dbId mappings between SVF2 (viewer) and OBJ (precomputed) formats
 */
function buildDbIdMappings(
  results: OperationResult[],
  model: Autodesk.Viewing.Model
): { svf2ToObj: Map<number, number>; objToSvf2: Map<number, number> } {
  const svf2ToObj = new Map<number, number>();
  const objToSvf2 = new Map<number, number>();

  for (const result of results) {
    const svf2DbId = result[1].dbId;
    try {
      const objDbId = model.reverseMapDbId(svf2DbId);
      svf2ToObj.set(svf2DbId, objDbId);
      objToSvf2.set(objDbId, svf2DbId);
    } catch {
      // If mapping fails, skip this result
      console.warn(
        `[OnDemandGeometry] Failed to map dbId ${svf2DbId} to OBJ dbId`
      );
    }
  }

  return { svf2ToObj, objToSvf2 };
}

/**
 * Add results to enriched map with their original values
 */
function addOriginalResults(
  results: OperationResult[],
  enrichedMap: Map<string, OperationResult>
): void {
  for (const result of results) {
    const resultKey = `${result[1].modelUrn}:${result[1].dbId}:${result[1].algorithmType}`;
    enrichedMap.set(resultKey, result);
  }
}

/**
 * Fetch geometry for a group of results with the same model and algorithm
 */
async function fetchGeometryForGroup(
  modelUrn: string,
  algorithmType: string,
  results: OperationResult[],
  modelId: string,
  model: Autodesk.Viewing.Model,
  client: Client,
  enrichedMap: Map<string, OperationResult>
): Promise<void> {
  const { svf2ToObj } = buildDbIdMappings(results, model);
  const objDbIds = Array.from(new Set(svf2ToObj.values()));

  if (objDbIds.length === 0) {
    addOriginalResults(results, enrichedMap);
    return;
  }

  try {
    const response = await client
      .query(GetAlgorithmGeometryDocument, {
        modelId,
        algorithmType,
        dbIds: objDbIds,
      })
      .toPromise();

    const geometryResults = response.data?.getAlgorithmGeometry ?? [];
    const geometryByObjDbId = new Map(geometryResults.map((g) => [g.dbId, g]));

    for (const result of results) {
      const [scaledResult, context] = result;
      const resultKey = `${context.modelUrn}:${context.dbId}:${context.algorithmType}`;
      const objDbId = svf2ToObj.get(context.dbId);
      const geometry =
        objDbId !== undefined ? geometryByObjDbId.get(objDbId) : undefined;

      if (geometry && scaledResult) {
        enrichedMap.set(resultKey, [
          mergeGeometry(scaledResult, geometry),
          context,
        ]);
      } else {
        enrichedMap.set(resultKey, result);
      }
    }
  } catch (error) {
    console.warn(
      `[OnDemandGeometry] Failed to fetch geometry for ${algorithmType}:`,
      error
    );
    addOriginalResults(results, enrichedMap);
  }
}

interface OnDemandGeometryVisualizationProps {
  /**
   * Operation results to visualize. May or may not have geometry attached.
   */
  operationResults: OperationResult[];
  /**
   * If true, skip on-demand fetching and use results as-is
   */
  skipFetching?: boolean;
}

/**
 * A wrapper around GeometricResultVisualization that fetches geometry on-demand.
 *
 * This component:
 * 1. Checks if operation results already have geometry
 * 2. For results without geometry, fetches it from the backend
 * 3. Passes the enriched results to GeometricResultVisualization
 */
export function OnDemandGeometryVisualization({
  operationResults,
  skipFetching = false,
}: OnDemandGeometryVisualizationProps) {
  const client = useClient();
  const { modelUrnToId } = useMeshAlgorithmResults();
  const { loadedModels } = useViewer();
  const [enrichedResults, setEnrichedResults] =
    useState<OperationResult[]>(operationResults);
  const [loading, setLoading] = useState(false);

  // Split results into those with and without geometry
  const { withGeometry, needsGeometry } = useMemo(() => {
    const withGeometry: OperationResult[] = [];
    const needsGeometry: OperationResult[] = [];

    for (const result of operationResults) {
      if (hasGeometry(result)) {
        withGeometry.push(result);
      } else if (result[0] !== undefined && result[1]?.algorithmType) {
        needsGeometry.push(result);
      }
    }

    return { withGeometry, needsGeometry };
  }, [operationResults]);

  // Fetch geometry for results that don't have it
  const fetchMissingGeometry = useCallback(async () => {
    if (needsGeometry.length === 0 || skipFetching) {
      setEnrichedResults(operationResults);
      return;
    }

    setLoading(true);

    try {
      // Group results by modelUrn and algorithmType for batch fetching
      const byModelAndAlgorithm = groupBy(
        needsGeometry,
        ([, context]) => `${context.modelUrn}:${context.algorithmType}`
      );

      const enrichedMap = new Map<string, OperationResult>();

      // Fetch geometry for each group
      await Promise.all(
        Object.entries(byModelAndAlgorithm).map(async ([key, results]) => {
          const [modelUrn, algorithmType] = key.split(':');
          const modelId = modelUrnToId.get(modelUrn);
          const model = loadedModels?.[modelUrn];

          if (!modelId || !algorithmType || !model) {
            addOriginalResults(results, enrichedMap);
            return;
          }

          await fetchGeometryForGroup(
            modelUrn,
            algorithmType,
            results,
            modelId,
            model,
            client,
            enrichedMap
          );
        })
      );

      // Combine results with geometry and newly enriched results
      const allEnriched = [
        ...withGeometry,
        ...Array.from(enrichedMap.values()),
      ];

      console.log(
        `[OnDemandGeometry] Fetched geometry for ${enrichedMap.size} results`
      );

      setEnrichedResults(allEnriched);
    } catch (error) {
      console.error('[OnDemandGeometry] Error fetching geometry:', error);
      setEnrichedResults(operationResults);
    } finally {
      setLoading(false);
    }
  }, [
    needsGeometry,
    withGeometry,
    operationResults,
    modelUrnToId,
    loadedModels,
    client,
    skipFetching,
  ]);

  // Fetch geometry when results change
  useEffect(() => {
    fetchMissingGeometry();
  }, [fetchMissingGeometry]);

  // Show loading indicator or render visualization
  if (loading && enrichedResults.length === 0) {
    return null; // Or a loading indicator
  }

  return <GeometricResultVisualization operationResults={enrichedResults} />;
}
